import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FlagIcon, Undo2Icon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/base/button";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "@/components/base/dropdown-menu";
import { Modal, ModalHeading } from "@/components/base/modal";
import { addToast } from "@/components/base/toast";
import { TeamNames } from "@/components/teams/names";
import type { PlayerProfile } from "@/db/schema";
import { useTRPC } from "@/integrations/trpc/react";
import { invalidateMatchQueries } from "@/lib/tournament-cache";

type MatchIdProps = {
  poolMatchId?: number;
  playoffMatchId?: number;
};

type TeamInfo = {
  id: number;
  team: {
    players: {
      profile: Pick<
        PlayerProfile,
        "id" | "preferredName" | "firstName" | "lastName"
      >;
    }[];
  };
};

type ForfeitMatchMenuProps = MatchIdProps & {
  teamA: TeamInfo | null;
  teamB: TeamInfo | null;
  status: string;
};

export function ForfeitMatchMenu({
  poolMatchId,
  playoffMatchId,
  teamA,
  teamB,
  status,
}: ForfeitMatchMenuProps) {
  const [forfeitTeam, setForfeitTeam] = useState<TeamInfo | null>(null);
  const [showUnforfeit, setShowUnforfeit] = useState(false);

  if (status === "completed") {
    return (
      <>
        <Button
          size="xs"
          variant="text"
          className="text-amber-500 hover:text-amber-600"
          onPress={() => setShowUnforfeit(true)}
          tooltip="Undo forfeit / reset match"
        >
          <Undo2Icon size={14} />
        </Button>
        {showUnforfeit && (
          <UnforfeitMatchModal
            poolMatchId={poolMatchId}
            playoffMatchId={playoffMatchId}
            isOpen={showUnforfeit}
            onOpenChange={setShowUnforfeit}
          />
        )}
      </>
    );
  }

  if (!teamA || !teamB) return null;

  return (
    <>
      <DropdownMenu
        label="Forfeit"
        tooltip="Forfeit a team"
        buttonVariant="text"
        buttonSize="xs"
        buttonClassName="text-red-500 hover:text-red-600"
        buttonIcon={<FlagIcon size={14} />}
      >
        <DropdownMenuItem onAction={() => setForfeitTeam(teamA)}>
          Forfeit{" "}
          <TeamNames
            players={teamA.team.players}
            link={false}
            showFirst={false}
          />
        </DropdownMenuItem>
        <DropdownMenuItem onAction={() => setForfeitTeam(teamB)}>
          Forfeit{" "}
          <TeamNames
            players={teamB.team.players}
            link={false}
            showFirst={false}
          />
        </DropdownMenuItem>
      </DropdownMenu>
      {forfeitTeam && (
        <ForfeitMatchModal
          poolMatchId={poolMatchId}
          playoffMatchId={playoffMatchId}
          forfeitTeam={forfeitTeam}
          isOpen={!!forfeitTeam}
          onOpenChange={(open) => {
            if (!open) setForfeitTeam(null);
          }}
        />
      )}
    </>
  );
}

function ForfeitMatchModal({
  poolMatchId,
  playoffMatchId,
  forfeitTeam,
  isOpen,
  onOpenChange,
}: MatchIdProps & {
  forfeitTeam: TeamInfo;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const { mutate, failureReason, isPending } = useMutation({
    ...trpc.tournaments.forfeitMatch.mutationOptions(),
    onSuccess: () => {
      invalidateMatchQueries(queryClient, trpc);
      onOpenChange(false);
      addToast({
        variant: "success",
        title: "Match forfeited",
      });
    },
  });

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <ModalHeading>Forfeit Match</ModalHeading>

        <p>
          Are you sure you want to forfeit this match for{" "}
          <TeamNames
            className="font-semibold italic"
            players={forfeitTeam.team.players}
            link={false}
          />
          ?
        </p>
        <p className="text-sm text-gray-500">
          The opponent will be awarded the win.
        </p>

        {failureReason && (
          <p className="text-sm text-red-600">{failureReason.message}</p>
        )}

        <div className="flex justify-end gap-2">
          <Button onPress={() => onOpenChange(false)}>Cancel</Button>
          <Button
            variant="solid"
            color="danger"
            isDisabled={isPending}
            onPress={() =>
              mutate({
                poolMatchId,
                playoffMatchId,
                forfeitTeamId: forfeitTeam.id,
              })
            }
          >
            {isPending ? "Forfeiting..." : "Forfeit"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function UnforfeitMatchModal({
  poolMatchId,
  playoffMatchId,
  isOpen,
  onOpenChange,
}: MatchIdProps & {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const { mutate, failureReason, isPending } = useMutation({
    ...trpc.tournaments.unforfeitMatch.mutationOptions(),
    onSuccess: () => {
      invalidateMatchQueries(queryClient, trpc);
      onOpenChange(false);
      addToast({
        variant: "success",
        title: "Match reset",
        description: "Match has been reset to scheduled.",
      });
    },
  });

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <ModalHeading>Reset Match</ModalHeading>

        <p>
          Are you sure you want to reset this match? All scores will be cleared
          and the match will return to scheduled status.
        </p>

        {failureReason && (
          <p className="text-sm text-red-600">{failureReason.message}</p>
        )}

        <div className="flex justify-end gap-2">
          <Button onPress={() => onOpenChange(false)}>Cancel</Button>
          <Button
            variant="solid"
            color="danger"
            isDisabled={isPending}
            onPress={() => mutate({ poolMatchId, playoffMatchId })}
          >
            {isPending ? "Resetting..." : "Reset Match"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
